import ReactGA from 'react-ga4'

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined

/**
 * Initialize Google Analytics 4
 * Call this once in main.tsx
 */
export const initGA = () => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gtagOptions: {
        debug_mode: import.meta.env.DEV,
      },
    })
    console.log('GA4 initialized:', GA_MEASUREMENT_ID)
  } else if (import.meta.env.DEV) {
    console.warn('GA4 Measurement ID not configured. Set VITE_GA_MEASUREMENT_ID in .env')
  }
}

/**
 * Check if GA4 is initialized
 */
export const isGAInitialized = (): boolean => !!GA_MEASUREMENT_ID

// ============ Page Tracking ============

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string) => {
  if (!isGAInitialized()) return
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title,
  })
}

// ============ Authentication Events ============

/**
 * Track user login
 */
export const trackLogin = (method: 'email' | 'phone' | 'google') => {
  if (!isGAInitialized()) return
  ReactGA.event('login', {
    login_method: method,
  })
}

/**
 * Track user signup
 */
export const trackSignup = (params: {
  signup_method: 'email' | 'phone' | 'google'
  student_class?: string
  source?: string
}) => {
  if (!isGAInitialized()) return
  ReactGA.event('sign_up', {
    signup_method: params.signup_method,
    class: params.student_class,
    source: params.source,
  })
}

/**
 * Track user logout
 */
export const trackLogout = () => {
  if (!isGAInitialized()) return
  ReactGA.event({ category: 'Auth', action: 'logout' })
}

// ============ Dashboard Events ============

/**
 * Track dashboard view
 */
export const trackDashboardViewed = (params: {
  has_taken_test: boolean
  tests_taken_count: number
}) => {
  if (!isGAInitialized()) return
  ReactGA.event('dashboard_viewed', {
    has_taken_test: params.has_taken_test ? 'yes' : 'no',
    tests_taken_count: params.tests_taken_count,
  })
}

// ============ Test Events ============

/**
 * Track quick free test CTA click
 */
export const trackQuickFreeTestCtaClicked = () => {
  if (!isGAInitialized()) return
  ReactGA.event('quick_free_test_cta_clicked', {})
}

/**
 * Track quick test start
 */
export const trackQuickTestStart = () => {
  if (!isGAInitialized()) return
  ReactGA.event('quick_test_start', {})
}

/**
 * Track quick test complete
 */
export const trackQuickTestComplete = () => {
  if (!isGAInitialized()) return
  ReactGA.event('quick_test_complete', {})
}

/**
 * Track full free test CTA click
 */
export const trackFullFreeTestCtaClicked = () => {
  if (!isGAInitialized()) return
  ReactGA.event('full_free_test_cta_clicked', {})
}

/**
 * Track full test start
 */
export const trackFullTestStart = () => {
  if (!isGAInitialized()) return
  ReactGA.event('full_test_start', {})
}

/**
 * Track full test complete
 */
export const trackFullTestComplete = () => {
  if (!isGAInitialized()) return
  ReactGA.event('full_test_complete', {})
}

/**
 * Track locked test click
 */
export const trackLockedTestClicked = (testName: string) => {
  if (!isGAInitialized()) return
  ReactGA.event('locked_test_clicked', {
    test_name: testName,
  })
}

/**
 * Track test start (generic)
 */
export const trackTestStart = (testCode: string, testName?: string) => {
  if (!isGAInitialized()) return
  ReactGA.event('select_content', {
    content_type: 'test',
    item_id: testCode,
    item_name: testName,
  })
  ReactGA.event({
    category: 'Test',
    action: 'start_test',
    label: testCode,
  })
}

/**
 * Track test submission
 */
export const trackTestSubmit = (
  testCode: string,
  score: number,
  totalQuestions: number,
  attemptedQuestions: number
) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Test',
    action: 'submit_test',
    label: testCode,
    value: Math.round(score),
  })
  // Also send as a custom event with more details
  ReactGA.event('test_complete', {
    test_code: testCode,
    score: score,
    total_questions: totalQuestions,
    attempted_questions: attemptedQuestions,
    accuracy: totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0,
  })
}

/**
 * Track test abandonment
 */
export const trackTestAbandon = (testCode: string, questionsAnswered: number) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Test',
    action: 'abandon_test',
    label: testCode,
    value: questionsAnswered,
  })
}

/**
 * Track test timeout
 */
export const trackTestTimeout = (testCode: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Test',
    action: 'test_timeout',
    label: testCode,
  })
}

// ============ Question Events ============

/**
 * Track question answer
 */
export const trackQuestionAnswer = (
  testCode: string,
  questionNumber: number,
  timeSpentSeconds: number
) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Question',
    action: 'answer_question',
    label: `${testCode}_Q${questionNumber}`,
    value: timeSpentSeconds,
  })
}

/**
 * Track question marked for review
 */
export const trackMarkForReview = (testCode: string, questionNumber: number) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Question',
    action: 'mark_for_review',
    label: `${testCode}_Q${questionNumber}`,
  })
}

// ============ Syllabus Events ============

/**
 * Track syllabus node expansion
 */
export const trackSyllabusExpand = (nodeName: string, nodeType: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Syllabus',
    action: 'expand_node',
    label: nodeName,
  })
  ReactGA.event('select_content', {
    content_type: nodeType.toLowerCase(),
    item_id: nodeName,
  })
}

// ============ PYQ Events ============

/**
 * Track PYQ browse started
 */
export const trackPYQBrowseStarted = (browseType: 'year' | 'topic' | 'syllabus') => {
  if (!isGAInitialized()) return
  ReactGA.event('pyq_browse_started', {
    browse_type: browseType,
  })
}

/**
 * Track when user clicks show/hide solution & hits paywall
 */
export const trackPYQSolutionLockedView = (browseType: 'year' | 'topic', questionIndex: number) => {
  if (!isGAInitialized()) return
  ReactGA.event('pyq_solution_locked_view', {
    browse_type: browseType,
    question_index: questionIndex,
  })
}

// ============ Analytics Events ============

/**
 * Track analytics page viewed
 */
export const trackAnalyticsViewed = (analyticsType: 'past_exam' | 'my_performance') => {
  if (!isGAInitialized()) return
  ReactGA.event('analytics_viewed', {
    analytics_type: analyticsType,
  })
}

/**
 * Track when analytics content is shown locked/blurred
 */
export const trackAnalyticsLockedView = () => {
  if (!isGAInitialized()) return
  ReactGA.event('analytics_locked_view', {})
}

// ============ Navigation Events ============

/**
 * Track sidebar navigation
 */
export const trackNavigation = (destination: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Navigation',
    action: 'sidebar_click',
    label: destination,
  })
}

/**
 * Track button click
 */
export const trackButtonClick = (buttonName: string, context?: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Engagement',
    action: 'button_click',
    label: context ? `${buttonName}_${context}` : buttonName,
  })
}

// ============ Error Events ============

/**
 * Track error occurrence
 */
export const trackError = (errorType: string, errorMessage: string, context?: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Error',
    action: errorType,
    label: context ? `${context}: ${errorMessage}` : errorMessage,
  })
}

// ============ Payment Events ============

/**
 * Track when a free user sees a locked solution (legacy - use trackPYQSolutionLockedView for PYQ)
 */
export const trackSolutionLockedView = (questionId: string, testCode: string) => {
  if (!isGAInitialized()) return
  ReactGA.event({
    category: 'Payment',
    action: 'solution_locked_view',
    label: `${testCode}_${questionId}`,
  })
  ReactGA.event('solution_locked_view', {
    question_id: questionId,
    test_code: testCode,
  })
}

/**
 * Track when user clicks any upgrade CTA
 */
export const trackUpgradeCtaClicked = (source: 'pyq_solution' | 'analytics' | 'test_series' | string) => {
  if (!isGAInitialized()) return
  ReactGA.event('upgrade_cta_clicked', {
    source,
  })
}

/**
 * Track when Razorpay checkout is opened
 */
export const trackCheckoutStarted = (amount?: number) => {
  if (!isGAInitialized()) return
  ReactGA.event('checkout_started', {
    ...(amount && { value: amount, currency: 'INR' }),
  })
}

/**
 * Track successful payment
 */
export const trackPaymentSuccess = (orderId?: string, amount?: number) => {
  if (!isGAInitialized()) return
  ReactGA.event('payment_success', {
    ...(orderId && { transaction_id: orderId }),
    ...(amount && { value: amount, currency: 'INR' }),
  })
}

/**
 * Track when user abandons payment
 */
export const trackPaymentAbandoned = (orderId?: string, step?: string) => {
  if (!isGAInitialized()) return
  ReactGA.event('payment_abandoned', {
    ...(orderId && { order_id: orderId }),
    ...(step && { step }),
  })
}

// ============ Support Events ============

/**
 * Track when user clicks contact support
 */
export const trackContactSupportClicked = (channel: 'whatsapp' | 'form') => {
  if (!isGAInitialized()) return
  ReactGA.event('contact_support_clicked', {
    channel,
  })
}

// ============ Feedback Events ============

/**
 * Track when free test feedback modal is shown
 */
export const trackFeedbackShown = (testCode: string) => {
  if (!isGAInitialized()) return
  ReactGA.event('free_test_feedback_shown', {
    test_code: testCode,
  })
}

/**
 * Track when free test feedback is submitted
 */
export const trackFeedbackSubmitted = (testCode: string) => {
  if (!isGAInitialized()) return
  ReactGA.event('free_test_feedback_submitted', {
    test_code: testCode,
  })
}

// ============ User Properties ============

/**
 * Set user ID for cross-device tracking
 */
export const setUserId = (userId: string) => {
  if (!isGAInitialized()) return
  ReactGA.set({ userId })
}

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, string | number | boolean>) => {
  if (!isGAInitialized()) return
  ReactGA.set(properties)
}
